import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, LogOut } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/analytics";
import { useAuth } from "../lib/auth-context";
import { BrandMark } from "../components/Brand";

export default function Onboarding() {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/workspaces/me", { name });
      track("workspace_created", { plan: "free", role: "admin" });
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create workspace");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50" data-testid="onboarding-page">
      <header className="border-b border-ink-200 bg-white">
        <div className="hp-container flex h-16 items-center justify-between">
          <BrandMark />
          <button
            onClick={async () => { await logout(); navigate("/signin"); }}
            className="hp-btn-ghost"
            data-testid="onboarding-signout"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>
      <main className="flex items-start justify-center px-6 py-20">
        <div className="w-full max-w-[460px]">
          <div className="hp-section-label">Welcome, {user?.name}</div>
          <h1 className="font-display mt-3 text-3xl font-medium tracking-tight text-ink-900">
            Name your workspace
          </h1>
          <p className="mt-2 text-[14px] text-ink-500">
            This is the shared space your team will use to record and review interviews.
          </p>
          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="onboarding-form">
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Workspace name
              </label>
              <input
                type="text"
                required
                placeholder="Acme HR"
                className="hp-input mt-1.5"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="onboarding-name"
                autoFocus
              />
            </div>
            {error && (
              <div
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700"
                data-testid="onboarding-error"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="hp-btn-primary w-full"
              data-testid="onboarding-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
              Continue
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
